import React from "react";
import {
    Flag, Clock, AlertTriangle, PauseCircle, CheckCircle2, HelpCircle,
} from "lucide-react";
import {
    SLA_STATUS_LABELS, OWNER_ROLE_LABELS, formatTimeInStage,
} from "@/lib/stages";

/**
 * OPS-004B · Canonical stage summary — Operations project detail.
 *
 * Renders exactly what `hydrate_stage_view` produces on the backend:
 * `current_stage`, `stage_label`, `stage_entered_at`,
 * `time_in_stage`, `expected_next_action`,
 * `expected_next_owner_role`, `stage_target_at`,
 * `stage_sla_status`, `is_stage_stalled`. No client-side SLA maths.
 */
export default function StageSummarySection({ project, nextActionTaskId = null, onOpenNextAction = null }) {
    if (!project) return null;
    const stageKey = project.current_stage || "unknown";
    // OPS-004B1 · Backend hydrates `stage_label`. No parallel map here.
    const stageLabel = project.stage_label || stageKey;
    const enteredAt = project.stage_entered_at;
    const timeInStage = project.time_in_stage;
    const nextAction = project.expected_next_action || "—";
    const ownerRole = project.expected_next_owner_role;
    const targetAt = project.stage_target_at;
    const sla = SLA_STATUS_LABELS[project.stage_sla_status] || null;
    const stalled = !!project.is_stage_stalled;
    const isOnHold = !!project.stage_is_on_hold || stageKey === "on_hold";
    const isCompleted = !!project.stage_is_terminal || stageKey === "completed";

    const StageIcon = isCompleted ? CheckCircle2
                    : isOnHold    ? PauseCircle
                    : stageKey === "unknown" ? HelpCircle
                    : Flag;

    return (
        <section
            data-testid="ops-project-stage-summary"
            className="rounded-xl border border-[#E5E7EB] bg-white p-5"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.14em] uppercase text-[#4B5565] font-medium flex items-center gap-1.5">
                        <StageIcon size={11} strokeWidth={1.75} /> Current stage
                    </div>
                    <div
                        data-testid="ops-project-stage-summary-current-stage"
                        className="mt-1 text-base font-medium text-[#131B2F]"
                    >
                        {stageLabel}
                    </div>
                    {enteredAt && (
                        <div className="mt-0.5 text-[11px] text-[#4B5565]">
                            Entered {formatDate(enteredAt)}
                            {timeInStage && (
                                <span data-testid="ops-project-stage-summary-time-in-stage">
                                    {" · "}{formatTimeInStage(timeInStage)} in stage
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex items-start gap-2 shrink-0">
                    {sla && (
                        <SlaChip
                            tone={sla.tone}
                            label={sla.label}
                            testid="ops-project-stage-summary-sla-chip"
                        />
                    )}
                    {stalled && (
                        <span
                            data-testid="ops-project-stage-summary-stalled"
                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-[#FEE2E2] text-[#B91C1C] border border-[#FCA5A5]"
                        >
                            <AlertTriangle size={11} strokeWidth={2} /> Stalled
                        </span>
                    )}
                </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div>
                    <div className="text-[#4B5565]">Expected next action</div>
                    {onOpenNextAction && nextActionTaskId ? (
                        <button
                            type="button"
                            onClick={onOpenNextAction}
                            data-testid="ops-project-stage-summary-next-action"
                            data-next-action-task-id={nextActionTaskId}
                            aria-label={`Open task ${nextAction}`}
                            title={`Open the task "${nextAction}"`}
                            className="mt-0.5 inline-flex items-center gap-1 text-left text-[#131B2F] font-medium underline decoration-dotted underline-offset-2 hover:text-[#3E9E2E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#131B2F]/25 rounded"
                        >
                            {nextAction}
                        </button>
                    ) : (
                        <div
                            data-testid="ops-project-stage-summary-next-action"
                            data-next-action-task-id={nextActionTaskId || undefined}
                            title={!nextActionTaskId && nextAction !== "—"
                                ? "No matching task for this action yet — check the task list below."
                                : undefined}
                            className="mt-0.5 text-[#131B2F] font-medium"
                        >
                            {nextAction}
                        </div>
                    )}
                </div>
                <div>
                    <div className="text-[#4B5565]">Expected next owner</div>
                    <div
                        data-testid="ops-project-stage-summary-next-owner"
                        className="mt-0.5 text-[#131B2F] font-medium"
                    >
                        {ownerRole ? (OWNER_ROLE_LABELS[ownerRole] || ownerRole) : "—"}
                    </div>
                </div>
                <div>
                    <div className="text-[#4B5565]">SLA target</div>
                    <div
                        data-testid="ops-project-stage-summary-sla-target"
                        className="mt-0.5 text-[#131B2F] font-medium flex items-center gap-1"
                    >
                        <Clock size={11} strokeWidth={1.75} className="text-[#4B5565]" />
                        {targetAt ? formatDate(targetAt) : "—"}
                    </div>
                </div>
            </div>
        </section>
    );
}

function SlaChip({ tone, label, testid }) {
    const cls = tone === "green"
        ? "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]"
        : tone === "amber"
        ? "bg-[#FEF3C7] text-[#B45309] border-[#FCD34D]"
        : tone === "red"
        ? "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]"
        : "bg-[#F5F5F7] text-[#4B5565] border-[#E5E7EB]";
    return (
        <span
            data-testid={testid}
            className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full border ${cls}`}
        >
            {label}
        </span>
    );
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch { return "—"; }
}
